import { useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { CalendarIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { Calendar } from "@component/calendar";
import { Button } from "@component/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@component/popover";
import { listAiqmTags, UNTAGGED_TAG } from "@service/aiqm/sentiment";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

export interface SentimentFilters {
    startDate?: string; // ISO YYYY-MM-DD
    endDate?: string; // ISO YYYY-MM-DD
    tags: string[];
}

// Tag filtering needs backend that persists tags + serves the tags endpoint
// (see BE_HANDOFF_AIQM_CALL_TAGS.md). Hide the tag filter until that ships so we
// don't offer filter options the backend can't honor. Matches the gate on the
// upload-side tag UI in training-aiqm/index.tsx.
const AIQM_TAGS_ENABLED =
    import.meta.env.VITE_AIQM_INSIGHTS_MOCK === "true" ||
    import.meta.env.VITE_AIQM_TAGS_ENABLED === "true";

interface SentimentFilterBarProps {
    value: SentimentFilters;
    onChange: (next: SentimentFilters) => void;
    disabled?: boolean;
    // Resolved date window from the BE aggregate response — used to show
    // "All time (X – Y)" in the picker button when no explicit dates are set.
    resolvedStartDate?: string;
    resolvedEndDate?: string;
}

function toISO(d: Date): string {
    // Local calendar date → YYYY-MM-DD (avoid UTC shift).
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function fromISO(s?: string): Date | undefined {
    if (!s) return undefined;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return undefined;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function formatRangeLabel(
    start?: string,
    end?: string,
    resolvedStart?: string,
    resolvedEnd?: string,
): string {
    const s = fromISO(start);
    const e = fromISO(end);
    const fmt = (d: Date) =>
        d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    if (s && e) return `${fmt(s)} – ${fmt(e)}`;
    if (s) return `From ${fmt(s)}`;
    if (e) return `Until ${fmt(e)}`;
    // "All time" — show the actual resolved window from the BE response so the
    // user knows what data range they're actually looking at.
    const rs = fromISO(resolvedStart);
    const re = fromISO(resolvedEnd);
    if (rs && re) return `All time (${fmt(rs)} – ${fmt(re)})`;
    return "All time";
}

// Quick presets for common manager questions ("how's the last month look").
const PRESETS: { label: string; days: number }[] = [
    { label: "Last 7 days", days: 7 },
    { label: "Last 30 days", days: 30 },
    { label: "Last 90 days", days: 90 },
];

function presetRange(days: number): { startDate: string; endDate: string } {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    return { startDate: toISO(start), endDate: toISO(end) };
}

export default function SentimentFilterBar({
    value,
    onChange,
    disabled,
    resolvedStartDate,
    resolvedEndDate,
}: SentimentFilterBarProps) {
    const [tags, setTags] = useState<string[]>([]);
    const [dateOpen, setDateOpen] = useState(false);
    const [tagOpen, setTagOpen] = useState(false);
    const didInitTags = useRef(false);

    useEffect(() => {
        if (!AIQM_TAGS_ENABLED) return;
        let active = true;
        listAiqmTags()
            .then((list) => {
                if (!active) return;
                setTags(list);
                // Default = every option selected, sent EXPLICITLY so the request
                // always carries the full tag set + include_untagged (never the
                // bare "no filter" form). Runs once, only if untouched.
                if (!didInitTags.current && value.tags.length === 0) {
                    didInitTags.current = true;
                    onChange({ ...value, tags: [UNTAGGED_TAG, ...list] });
                }
            })
            .catch((err) => {
                catchErrorLog(
                    `listAiqmTags (sentiment filter) failed: ${
                        err instanceof Error ? err.message : String(err)
                    }`
                );
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const range: DateRange | undefined = useMemo(() => {
        const from = fromISO(value.startDate);
        const to = fromISO(value.endDate);
        if (!from && !to) return undefined;
        return { from, to };
    }, [value.startDate, value.endDate]);

    // Selectable options: a "Blank" pseudo-tag (calls with no tags) pinned first,
    // then the real tags. An empty value.tags is the canonical "all selected"
    // default — every option shows checked and no tag filter is sent.
    const options = useMemo(() => [UNTAGGED_TAG, ...tags], [tags]);
    // "All selected" covers both the untouched default (empty = no filter) and an
    // explicit full selection — both mean every option is checked.
    const allSelected =
        value.tags.length === 0 || value.tags.length === options.length;
    const isChecked = (key: string) =>
        value.tags.length === 0 || value.tags.includes(key);

    const tagLabel = allSelected
        ? "All tags"
        : value.tags.length === 1
          ? value.tags[0] === UNTAGGED_TAG
              ? "Blank"
              : value.tags[0]
          : `${value.tags.length} selected`;

    function toggleTag(key: string) {
        // Expand the implicit "all" (empty) state to an explicit list before
        // toggling, then keep the selection explicit — even a full selection stays
        // a full list so its tags (+ include_untagged) are sent as request params,
        // rather than collapsing to "no filter".
        const base =
            value.tags.length === 0 ? [...options] : [...value.tags];
        const idx = base.indexOf(key);
        if (idx >= 0) base.splice(idx, 1);
        else base.push(key);
        // Unchecking the last option reads as "back to all tags" in the UI — keep
        // it an explicit full selection (all tags + include_untagged) so the
        // request matches, instead of an empty "no filter" call.
        onChange({ ...value, tags: base.length === 0 ? [...options] : base });
    }

    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-400">Filter by:</span>

            {/* Date range */}
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={disabled}
                        className="h-9 w-[320px] justify-between gap-1.5 text-sm font-normal"
                    >
                        <span className="flex items-center gap-1.5 truncate">
                            <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate" title={formatRangeLabel(value.startDate, value.endDate, resolvedStartDate, resolvedEndDate)}>
                                {formatRangeLabel(value.startDate, value.endDate, resolvedStartDate, resolvedEndDate)}
                            </span>
                        </span>
                        {(value.startDate || value.endDate) ? (
                            <span
                                role="button"
                                aria-label="Clear date filter"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange({ ...value, startDate: undefined, endDate: undefined });
                                }}
                                className="shrink-0 rounded p-0.5 hover:bg-gray-200"
                            >
                                <XIcon className="h-3 w-3 text-gray-400" />
                            </span>
                        ) : (
                            <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                    <div className="flex flex-wrap gap-1 border-b border-gray-100 p-2">
                        {PRESETS.map((p) => (
                            <button
                                key={p.label}
                                type="button"
                                onClick={() => {
                                    const r = presetRange(p.days);
                                    onChange({
                                        ...value,
                                        startDate: r.startDate,
                                        endDate: r.endDate,
                                    });
                                }}
                                className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <Calendar
                        mode="range"
                        selected={range}
                        onSelect={(r) => {
                            onChange({
                                ...value,
                                startDate: r?.from ? toISO(r.from) : undefined,
                                endDate: r?.to ? toISO(r.to) : undefined,
                            });
                        }}
                        disabled={{ after: new Date() }}
                        numberOfMonths={2}
                        toMonth={new Date()}
                    />
                </PopoverContent>
            </Popover>

            {/* Tag multi-select — hidden until the backend persists tags */}
            {AIQM_TAGS_ENABLED && (
            <Popover open={tagOpen} onOpenChange={setTagOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={disabled}
                        className="h-9 w-[200px] justify-between gap-1.5 text-sm font-normal"
                    >
                        <span className="flex items-center gap-1.5 truncate">
                            <span className="truncate" title={tagLabel}>{tagLabel}</span>
                            {value.tags.length > 0 && (
                                <span className="shrink-0 rounded-full bg-blue-100 px-1.5 text-[10px] font-semibold text-blue-700">
                                    {value.tags.length}
                                </span>
                            )}
                        </span>
                        {!allSelected ? (
                            <span
                                role="button"
                                aria-label="Clear tag filter"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange({ ...value, tags: [...options] });
                                }}
                                className="shrink-0 rounded p-0.5 hover:bg-gray-200"
                            >
                                <XIcon className="h-3 w-3 text-gray-400" />
                            </span>
                        ) : (
                            <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0" align="end">
                    <div className="flex items-center justify-between border-b border-gray-100 px-2 py-1.5">
                        <button
                            type="button"
                            onClick={() => onChange({ ...value, tags: [...options] })}
                            className="rounded px-1.5 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
                        >
                            Select all
                        </button>
                        <span className="px-1.5 text-[11px] text-gray-400">
                            {allSelected ? options.length : value.tags.length}/{options.length}
                        </span>
                    </div>
                    <div className="max-h-72 overflow-y-auto p-1">
                        {options.map((key) => {
                            const checked = isChecked(key);
                            const isBlank = key === UNTAGGED_TAG;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => toggleTag(key)}
                                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-50"
                                >
                                    <span
                                        className={cn(
                                            "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border",
                                            checked
                                                ? "border-blue-600 bg-blue-600 text-white"
                                                : "border-gray-300"
                                        )}
                                    >
                                        {checked && (
                                            <svg
                                                viewBox="0 0 12 12"
                                                className="h-3 w-3"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M2.5 6.5 5 9l4.5-5" />
                                            </svg>
                                        )}
                                    </span>
                                    <span
                                        className={cn(
                                            "truncate",
                                            isBlank ? "italic text-gray-500" : "text-gray-700"
                                        )}
                                        title={isBlank ? "Calls with no tags" : key}
                                    >
                                        {isBlank ? "Blank" : key}
                                    </span>
                                    {isBlank && (
                                        <span className="ml-auto text-[10px] text-gray-400">
                                            no tags
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>
            )}

        </div>
    );
}
